import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { priceSimulatorDataContext } from 'contexts/PriceSimulatorContext';
import { CustomSelect } from 'components/pages/GrowthAnalysis';
import DownSvg from 'assets/svg/Down';
import { CustomTable } from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import { getNewKpiUpdateColumns } from 'utils/getPriceSimulatorColumns';
import _ from 'lodash';
import { getPercentageChangeV2, scaleMaxValue } from 'utils/helperMethods';
import { useFetchPortfolioMetricsForInput, useFetchPortfolioRecommendationMetrics } from 'hooks/priceSimulator';
import { FilterContextData } from 'contexts/FilterContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PSR_PROTFOLIO_ADD_CELL_INPUT_PERCENTAGE_CHANGED,
    PSR_PROTFOLIO_ADD_CELL_INPUT_VALUE_CHANGED,
    PSR_PROTFOLIO_ADD_CELL_LEVEL1_DP_CHANGED,
    PSR_PROTFOLIO_ADD_CELL_LEVEL2_DP_CHANGED,
} from 'utils/mixpanelEventNames';

type AddMissingPPGModalProps = {
    visible: boolean;
    closeModal: () => void;
    isEditMode: boolean;
};

const AddMissingPPGModal = ({ visible, closeModal, isEditMode }: AddMissingPPGModalProps) => {
    const {
        newPortfolioUpdateKpis,
        portfolioUpdateKpisData,
        setNewPortfolioUpdateKpis,
        viewMode,
        simulationDateRange,
        addNewPPGDetails,
        expandedRowDetails,
        simulationTimePeriodSelection,
        levels,
        selectedDataSource,
    } = useContext(priceSimulatorDataContext);
    const { idForRestOfComponent } = useContext(FilterContextData);

    const [availalablePPG, setAvailablePPG] = useState([]);
    const [selectedPPG, setSelectedPPG] = useState<any>(null);
    const [availalableRetailer, setAvailableRetailer] = useState([]);
    const [selectedRetailer, setSelectedRetailer] = useState<any>(null);

    const {
        loading: portfolioRecommendationMetricsLoading,
        data: portfolioRecommendationMetricsData,
        error: portfolioRecommendationMetricsError,
        fetchPortfolioRecommendationMetrics,
    } = useFetchPortfolioRecommendationMetrics();

    const {
        loading: portfolioMetricsForInputLoading,
        data: portfolioMetricsForInputData,
        error: portfolioMetricsForInputError,
        fetchPortfolioMetricsForInput,
    } = useFetchPortfolioMetricsForInput();

    useEffect(() => {
        if (selectedPPG && selectedRetailer && !isEditMode) {
            fetchPortfolioRecommendationMetrics({
                variables: {
                    selectedRow: selectedPPG.label,
                    selectedColumn: selectedRetailer.label,
                    rowAttributeName: levels[0],
                    colAttributeName: levels[1],
                    filters: JSON.stringify(idForRestOfComponent),
                    timePeriod: String(simulationTimePeriodSelection).toLowerCase(),
                    dataSourceType: selectedDataSource.value,
                },
            });
        }
    }, [selectedPPG, selectedRetailer]);

    useEffect(() => {
        if (portfolioRecommendationMetricsData) {
            newPortfolioUpdateKpis.forEach((element: any) => {
                element.rval = portfolioRecommendationMetricsData[element.key];
                element.value = '';
                element.percentChange = '';
            });

            setNewPortfolioUpdateKpis([...newPortfolioUpdateKpis]);
        }
    }, [portfolioRecommendationMetricsData]);

    useEffect(() => {
        if (isEditMode) {
            const edittingItem = portfolioUpdateKpisData?.find(
                (item: any) => item.label === expandedRowDetails.rowName,
            )[expandedRowDetails.columnName];

            const newData = _.clone(newPortfolioUpdateKpis);

            newData.forEach((item: any) => {
                const target = edittingItem.inputList.find((t: any) => t.kpiName === item.key);
                item.value = target.kpiValue;
                item.rval = target.rval;
                item.percentChange = target.percentChange;
            });

            setNewPortfolioUpdateKpis(newData);
        }
    }, [isEditMode]);

    useEffect(() => {
        if (portfolioUpdateKpisData) {
            const missingPPGs: any = [];
            const keys = Object.keys(portfolioUpdateKpisData[0] || {}).filter(
                (key: string) => !['key', 'kpiList', 'label'].includes(key),
            );
            portfolioUpdateKpisData?.forEach((item: any) => {
                for (let i = 0; i < keys.length; i++) {
                    if (item[keys[i]].value === null) {
                        missingPPGs.push({ label: item.label, value: item.key });
                        break;
                    }
                }
            });

            setAvailablePPG(missingPPGs);
            if (addNewPPGDetails.columnName) {
                setSelectedPPG({ label: addNewPPGDetails.rowName, value: addNewPPGDetails.rowIndex + 1 });
                setSelectedRetailer({ label: addNewPPGDetails.columnName, value: addNewPPGDetails.columnName });
            }
        }
    }, [addNewPPGDetails]);

    useEffect(() => {
        if (selectedPPG) {
            const selectedPPGWithDetails = portfolioUpdateKpisData.find((item: any) => item.key === selectedPPG?.value);
            const missingRetailers: any = [];
            const keys = Object.keys(selectedPPGWithDetails || {}).filter(
                (key: string) => !['key', 'kpiList', 'label'].includes(key),
            );
            keys.forEach((item: any) => {
                if (selectedPPGWithDetails[item].value === null) {
                    missingRetailers.push({ label: item, value: item });
                }
            });
            setAvailableRetailer(missingRetailers);
        }
    }, [selectedPPG]);

    // on data success we will close the modal
    useEffect(() => {
        if (portfolioMetricsForInputData) {
            closeModal();

            // reset the state
            const newData = newPortfolioUpdateKpis.map((item: any) => {
                return {
                    ...item,
                    percentChange: '',
                    value: 0,
                    rval: 0,
                };
            });

            setNewPortfolioUpdateKpis(newData);
        }
    }, [portfolioMetricsForInputData]);

    const onApplyChange = () => {
        fetchPortfolioMetricsForInput({
            variables: {
                inputList: [
                    ...newPortfolioUpdateKpis.map((i: any) =>
                        JSON.stringify({
                            kpiName: i.key,
                            kpiValue: _.round(Number(i.value || i.rval), 2), // take recommendation value if user is not provided with value
                            percentChange: _.round(i.percentChange, 2),
                            rval: _.round(i.rval, 2),
                        }),
                    ),
                ],
            },
        });
    };

    const onResetClick = () => {
        closeModal();
    };

    const handleInputValuesChanges = (value: any, key: any, isNewValue: boolean) => {
        if (isNaN(value)) return;
        const newData = [...newPortfolioUpdateKpis];
        const target = newData.find((item) => key === item.key);
        if (target) {
            target.value = isNewValue
                ? value
                : value === ''
                ? ''
                : _.round(scaleMaxValue(value, Number(target.rval) || 0), 2);
            target.percentChange = !isNewValue
                ? value
                : value === ''
                ? ''
                : getPercentageChangeV2(Number(target.rval) || 0, value);
        }

        if (['retail_price', 'volume', 'units'].includes(key)) {
            const newValue = isNewValue ? value : target.value;
            cascadeChanges(key, Number(newValue), newData);
        }
        setNewPortfolioUpdateKpis(newData);

        if (isNewValue) {
            trackEventMixpanel(PSR_PROTFOLIO_ADD_CELL_INPUT_VALUE_CHANGED, {
                label: key,
                value,
            });
        } else {
            trackEventMixpanel(PSR_PROTFOLIO_ADD_CELL_INPUT_PERCENTAGE_CHANGED, {
                label: key,
                value,
            });
        }
    };

    const cascadeChanges = (sourceColumn: string, inputValue: number, data: any) => {
        const volume = data[0];
        const value = data[1];
        const units = data[2];
        const retailerPrice = data[5];

        if (sourceColumn === 'retail_price') {
            value.value = _.round(inputValue * (units.value || units.rval), 2);
            value.percentChange = _.round(getPercentageChangeV2(value.rval, value.value), 2);
        } else if (sourceColumn === 'volume') {
            const volumnUnitsRatioFactor = volume.rval / units.rval;
            units.value = _.round(inputValue / volumnUnitsRatioFactor, 2);
            units.percentChange = _.round(getPercentageChangeV2(units.rval, units.value), 2);
            value.value = _.round((retailerPrice.value || retailerPrice.rval) * (units.value || units.rval), 2);
            value.percentChange = _.round(getPercentageChangeV2(value.rval, value.value), 2);
        } else if (sourceColumn === 'units') {
            value.value = _.round(inputValue * (retailerPrice.value || retailerPrice.rval), 2);
            value.percentChange = _.round(getPercentageChangeV2(value.rval, value.value), 2);
            const volumnUnitsRatioFactor = volume.rval / units.rval;
            volume.value = inputValue * volumnUnitsRatioFactor;
            volume.percentChange = _.round(getPercentageChangeV2(volume.rval, volume.value), 2);
        }
    };

    const newKpiUpdateColumns = useMemo(() => {
        return getNewKpiUpdateColumns(
            viewMode || simulationDateRange?.includes(null),
            handleInputValuesChanges,
            simulationDateRange,
        );
    }, [newPortfolioUpdateKpis]);

    const RenderPPGDropdown = () => {
        return (
            <div className="flex mr-4 items-center">
                <span className="mr-4 font-xxs">Select PPG</span>
                <CustomSelect
                    style={{ marginRight: 16 }}
                    suffixIcon={<DownSvg />}
                    disabled={false}
                    placeholder="Select PPG"
                    value={selectedPPG}
                    options={availalablePPG}
                    className="dropdown-height dropdown-width overflow-hidden uniqSelPPGModelDropdown"
                    onChange={(val: any, item: any) => {
                        setSelectedPPG(item);
                        setSelectedRetailer(null);
                        trackEventMixpanel(PSR_PROTFOLIO_ADD_CELL_LEVEL1_DP_CHANGED, {
                            label: item.label,
                        });
                    }}
                />
            </div>
        );
    };

    const RenderRetailerDropdown = () => {
        return (
            <div className="flex mr-4 items-center">
                <span className="mr-4 font-xxs">Select Retailer</span>
                <CustomSelect
                    suffixIcon={<DownSvg />}
                    disabled={false}
                    placeholder="Select Retailer"
                    value={selectedRetailer}
                    options={availalableRetailer}
                    className="dropdown-height dropdown-width overflow-hidden uniqSelPPGRetailerDropdown"
                    onChange={(val: any, item: any) => {
                        setSelectedRetailer(item);
                        trackEventMixpanel(PSR_PROTFOLIO_ADD_CELL_LEVEL2_DP_CHANGED, {
                            label: item.label,
                        });
                    }}
                />
            </div>
        );
    };

    const commonLoader = portfolioRecommendationMetricsLoading || portfolioMetricsForInputLoading;

    const isAddBtnDisabled = commonLoader || !selectedRetailer;

    return (
        <StyledModal
            open={visible}
            title="Add PPG"
            width={600}
            onCancel={closeModal}
            footer={null}
            className="rounded "
            centered
        >
            <div className="rounded overflow-hidden">
                <div className="flex justify-between items-center mb-4">
                    <RenderPPGDropdown />
                    <RenderRetailerDropdown />
                </div>

                {selectedRetailer ? (
                    <CustomTable
                        dataSource={newPortfolioUpdateKpis}
                        columns={newKpiUpdateColumns as any}
                        bordered={false}
                        pagination={false}
                        scroll={{ x: '100%', y: 225 }}
                        sticky
                        loading={commonLoader}
                    />
                ) : (
                    <div className="flex items-center justify-center h-72 border  border-purpleFont rounded">
                        <span className="text-fontGreyAlt1">Please select retailer</span>
                    </div>
                )}
            </div>
            <div className="flex justify-end pt-6 font-bold">
                <button
                    type="button"
                    className="mr-2 px-6 py-1 rounded cursor-pointer text-purpleFont bg-white border border-purpleFont font-xxs"
                    onClick={onResetClick}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className={`text-white  px-6 py-1 rounded border font-xxs ${
                        isAddBtnDisabled
                            ? 'cursor-not-allowed border-borderGrey bg-borderGrey text-promoColour'
                            : 'cursor-pointer border-purpleFont  bg-purpleFont text-white'
                    }`}
                    onClick={onApplyChange}
                    disabled={isAddBtnDisabled}
                >
                    Add
                </button>
            </div>
        </StyledModal>
    );
};

export default AddMissingPPGModal;

const StyledModal = styled(Modal)`
    .ant-modal-title {
        ${tw`text-subHeaderColor`}
    }

    .ant-modal-close-x span svg {
        fill: '#140B1D';
    }

    .ant-modal-header {
        border: 0px;
    }

    .ant-modal {
        ${tw`w-1/3`}
    }

    .ant-modal-body {
        ${tw`pt-0`}
    }

    .dropdown-width {
        width: 170px;
    }

    .ant-checkbox-checked .ant-checkbox-inner {
        ${tw`bg-purpleFont border-purpleFont`}
    }
`;
